import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "styled-components";
import {
  Axis,
  AxisTickStrategies,
  ColorHEX,
  ColorRGBA,
  ConstantLine,
  CustomTick,
  DashedLine,
  PointLineAreaSeries,
  SolidFill,
  SolidLine,
  StipplePatterns,
  Themes,
  UIDraggingModes,
  UIElement,
  UIOrigins,
  UIPointableTextBox,
  UIElementBuilders,
  emptyFill,
  emptyLine,
  lightningChart,
  synchronizeAxisIntervals,
} from "@lightningchart/lcjs";
import {
  ChartContainer,
  ChartSurface,
  CloseButton,
  OpenButton,
  LegendBar,
  LegendItem,
  LegendMarker,
  NoDataMessage,
} from "./LogChart.styles";
import {
  getUnitTranslation,
  getChartTitleTranslation,
  getCurveTermTranslation,
  isDateTimeMnemonic,
} from "../../translations/translations";

interface CurveSpecification {
  mnemonic: string;
  unit?: string;
}

interface LogChartData {
  curveSpecifications?: CurveSpecification[];
  data?: Record<string, unknown>[];
}

interface OverlayPoint {
  value: number;
  md: number;
  tvd?: number;
}

// A standalone line drawn against the depth (Y) axis of a measured-depth chart,
// sourced independently from the log rows (e.g. stim-job formation densities).
export interface LogChartOverlaySeries {
  mnemonic: string;
  unit?: string;
  // Unit of the point depths, so they can be converted to the chart's depth
  // unit the same way the log's index curve is.
  depthUnit?: string;
  points: OverlayPoint[];
}

interface LogChartProps {
  data: LogChartData | null | undefined;
  title: string;
  emptyMessage?: string;
  indexCurve?: string;
  autoFitKey?: string;
  storageKey?: string;
  hiddenSeriesOverride?: Record<string, boolean>;
  highlightedSeriesMnemonic?: string;
  overlaySeries?: LogChartOverlaySeries[];
  // Measured depths (in the chart's depth unit) drawn as dashed horizontal
  // reference lines on the depth axis of the measured-depth (calculated) chart,
  // e.g. the shoe of the deepest casing. Ignored on non-depth charts.
  depthReferenceLines?: number[];
  // Decimal places per curve (lower-cased mnemonic) reported by the unit
  // system; curves without an entry keep the default formatting.
  curvePrecisions?: Record<string, number>;
  onHiddenSeriesChange?: (hiddenSeries: Record<string, boolean>) => void;
  onClose?: () => void;
  onOpen?: () => void;
  isDialog?: boolean;
}

type XKind = "date" | "number" | "category" | "index";

interface XDescriptor {
  field: string;
  kind: XKind;
  unit: string;
}

interface SeriesBinding {
  mnemonic: string;
  label: string;
  color: string;
  unit?: string;
  series: PointLineAreaSeries;
}

interface OverlayBinding {
  mnemonic: string;
  label: string;
  color: string;
  unit?: string;
  groupKey: string;
  points: OverlayPoint[];
  series: PointLineAreaSeries;
}

// A stage boundary: the vertical line plus the text box naming the new stage.
interface StageMarker {
  line: ConstantLine;
  tick: CustomTick;
}

interface ChartRuntime {
  series: SeriesBinding[];
  overlays: OverlayBinding[];
  // Discrete stage curve: drawn as boundary markers instead of a line series.
  stageField?: string;
  stageMarkers: StageMarker[];
  lastStageValue?: number;
  addStageMarker?: (value: number, label: string) => StageMarker;
  syncedLength: number;
  lastSyncedRow?: Record<string, unknown>;
  categoryLabels: string[];
  xAxis: Axis;
  // Redraws the dashed depth reference lines (casing shoe) and their floating
  // titles for the given depths/labels. Owned by the chart-creation effect so
  // it can hold the chart, axes, and listeners; called from the effect that
  // tracks the reference depths.
  syncDepthReferenceLines: (
    items: Array<{ value: number; label: string }>
  ) => void;
  userViewLocked: boolean;
  measuredDepthMode: boolean;
  xOrigin?: number;
  xStart?: number;
  xEnd?: number;
  // Ticks pinned to the first and last value of the log, so the ends of the
  // data are labelled even when they fall between the strategy's grid ticks.
  // Each edge holds one tick per label row.
  xEdgeTicks?: { start: CustomTick[]; end: CustomTick[] };
  xAxes: Array<{
    groupKey: string;
    axis: Axis;
    mnemonics: string[];
    side: "bottom" | "top";
  }>;
  xRanges: Map<string, { start: number; end: number }>;
  yAxis: Axis;
  yDescriptor: XDescriptor;
  yAxes: Array<{
    groupKey: string;
    axis: Axis;
    mnemonics: string[];
    side: "left" | "right";
  }>;
  yRanges: Map<string, { start: number; end: number }>;
}

export const LOG_CHART_COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFD016",
  "#4BC0C0",
  "#9966FF",
  "#FF9832",
  "#FF8A80",
  "#26A69A",
  "#FF8228",
  "#66BB6A",
  "#5C6BC0",
  "#EC407A",
];

const NORMAL_LINE_WIDTH = 1.5;
const HOVERED_LINE_WIDTH = 4;
const CURSOR_MODE = "show-all-interpolated" as const;
const REALTIME_SAMPLE_HEADROOM = 100_000;
const REALTIME_SAMPLE_MAX = 2_000_000;
const INITIAL_Y_TICK_SEGMENTS = 5;
const Y_AXIS_THICKNESS = { min: 30, max: 80 };
const AXIS_INTERACTION_THICKNESS = Y_AXIS_THICKNESS.max;
// Edge ticks get a long tick line so their labels sit on their own row below the
// grid labels and never collide with them.
const X_EDGE_TICK_LENGTH = 14;
const X_EDGE_TICK_ROW_HEIGHT = 11;
// Room for the tick line plus the two-row (date over time) edge label.
const X_EDGE_TICK_AXIS_THICKNESS = 56;
// Height of the strip above the plot that holds the stage labels.
const STAGE_LABEL_AXIS_THICKNESS = 26;

interface ChartThemeColors {
  background: ReturnType<typeof ColorRGBA>;
  axisLine: ReturnType<typeof ColorRGBA>;
  axisTitle: ReturnType<typeof ColorRGBA>;
  majorGrid: ReturnType<typeof ColorRGBA>;
  minorGrid: ReturnType<typeof ColorRGBA>;
  majorLabel: ReturnType<typeof ColorRGBA>;
  minorLabel: ReturnType<typeof ColorRGBA>;
  tooltipBackground: ReturnType<typeof ColorRGBA>;
  tooltipBorder: ReturnType<typeof ColorRGBA>;
  tooltipText: ReturnType<typeof ColorRGBA>;
  tooltipHeaderText: ReturnType<typeof ColorRGBA>;
  tooltipHeaderBackground: ReturnType<typeof ColorRGBA>;
  tooltipTickMarkerBackground: ReturnType<typeof ColorRGBA>;
  tooltipTickMarkerBorder: ReturnType<typeof ColorRGBA>;
  tooltipTickMarkerText: ReturnType<typeof ColorRGBA>;
  stageLine: ReturnType<typeof ColorRGBA>;
  casingLine: ReturnType<typeof ColorRGBA>;
  stageLabelBackground: ReturnType<typeof ColorRGBA>;
  stageLabelBorder: ReturnType<typeof ColorRGBA>;
  stageLabelText: ReturnType<typeof ColorRGBA>;
}

const isDateField = isDateTimeMnemonic;

const getMatchingField = (fields: string[], requested: string) =>
  fields.find((field) => field.toLowerCase() === requested.toLowerCase());

const shouldRedrawChartOnRealtimeUpdate = (title: string) =>
  title.trim().toLowerCase() === "calculated";

const isMeasuredDepthMnemonic = (value: string) =>
  ["measureddepth", "md"].includes(value.toLowerCase());

// The stage number is discrete: its own line carries no information, so it is
// rendered as a boundary marker at every change instead of a curve.
const isStageMnemonic = (value: string) =>
  value.toLowerCase() === "stagenumber";

const resolveStageField = (
  rows: Record<string, unknown>[],
  specs: CurveSpecification[]
) =>
  Object.keys(rows[0] || {}).find(isStageMnemonic) ||
  specs.find((spec) => isStageMnemonic(spec.mnemonic))?.mnemonic;

const getStageLabel = (value: number) => `Стадия ${value}`;

// Curves that get their own line series: everything except the axis curve and
// the stage number.
const isPlottableCurve = (
  spec: CurveSpecification,
  measuredDepthMode: boolean,
  axisField: string
) =>
  !isStageMnemonic(spec.mnemonic) &&
  (measuredDepthMode
    ? !isMeasuredDepthMnemonic(spec.mnemonic)
    : spec.mnemonic.toLowerCase() !== axisField.toLowerCase());

const clearStageMarkers = (runtime: ChartRuntime) => {
  runtime.stageMarkers.forEach(({ line, tick }) => {
    line.dispose();
    tick.dispose();
  });
  runtime.stageMarkers = [];
  runtime.lastStageValue = undefined;
};

// Marks every row where the stage number changes, at the index-axis position of
// that row. Only the newly synced rows are scanned, so realtime appends add
// markers without redrawing the existing ones.
const syncStageMarkers = (
  runtime: ChartRuntime,
  rows: Record<string, unknown>[],
  startIndex: number,
  axisValues: Float32Array
) => {
  const { stageField, addStageMarker } = runtime;
  if (!stageField || !addStageMarker) return;

  for (let index = 0; index < axisValues.length; index += 1) {
    const stage = toFiniteNumber(rows[startIndex + index][stageField]);
    if (!Number.isFinite(stage) || stage === runtime.lastStageValue) continue;
    // A change on a row without an axis value is picked up on the next row.
    const value = axisValues[index];
    if (!Number.isFinite(value)) continue;

    runtime.lastStageValue = stage;
    runtime.stageMarkers.push(addStageMarker(value, getStageLabel(stage)));
  }
};

const resolveMeasuredDepthDescriptor = (
  rows: Record<string, unknown>[],
  specs: CurveSpecification[]
): XDescriptor | undefined => {
  const fields = Object.keys(rows[0] || {});
  const field =
    fields.find(isMeasuredDepthMnemonic) ||
    specs.find((spec) => isMeasuredDepthMnemonic(spec.mnemonic))?.mnemonic;
  if (!field) return undefined;

  const spec = specs.find(
    (item) => item.mnemonic.toLowerCase() === field.toLowerCase()
  );

  return { field, kind: "number", unit: spec?.unit || "" };
};

const readStoredRecord = (key: string) => {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return {};
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, boolean>)
      : {};
  } catch {
    return {};
  }
};

const normalizeUnit = (unit?: string) => {
  const normalized = unit?.trim();
  if (!normalized || normalized.toLowerCase() === "unitless") return "";
  return getUnitTranslation(normalized);
};

const toThemeColor = (hex: string, alpha = 255) => {
  const value = hex.trim().replace("#", "");
  const normalized =
    value.length === 3
      ? value
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : value;
  const number = Number.parseInt(normalized, 16);

  if (!Number.isFinite(number)) {
    return ColorRGBA(255, 255, 255, alpha);
  }

  return ColorRGBA(
    (number >> 16) & 255,
    (number >> 8) & 255,
    number & 255,
    alpha
  );
};

const resolveXDescriptor = (
  rows: Record<string, unknown>[],
  specs: CurveSpecification[],
  requestedField: string
): XDescriptor => {
  const fields = Object.keys(rows[0] || {});
  const requested = getMatchingField(fields, requestedField);
  const dateTime = fields.find(isDateField);
  const fallbackSpec = specs.find((spec) => !isDateField(spec.mnemonic));
  const fallback = fallbackSpec
    ? getMatchingField(fields, fallbackSpec.mnemonic)
    : undefined;
  const field = requested || fallback || dateTime || "__index";
  const spec = specs.find(
    (item) => item.mnemonic.toLowerCase() === field.toLowerCase()
  );

  if (field === "__index") {
    return { field, kind: "index", unit: "" };
  }
  if (isDateField(field)) {
    return { field, kind: "date", unit: spec?.unit || "" };
  }

  const sample = rows
    .slice(0, 100)
    .map((row) => row[field])
    .find((value) => value !== null && value !== undefined && value !== "");

  if (sample === undefined) {
    return { field, kind: "category", unit: spec?.unit || "" };
  }

  const numericValue = Number(sample);
  if (typeof sample === "number" || (!isNaN(numericValue) && sample !== "")) {
    return { field, kind: "number", unit: spec?.unit || "" };
  }

  if (typeof sample === "string" && Number.isFinite(toTimestamp(sample))) {
    return { field, kind: "date", unit: spec?.unit || "" };
  }

  return { field, kind: "category", unit: spec?.unit || "" };
};

const toFiniteNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return Number.NaN;
  const number = Number(value);
  return Number.isFinite(number) ? number : Number.NaN;
};

const toTimestamp = (value: unknown) => {
  if (value === null || value === undefined || value === "") return Number.NaN;
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : Number.NaN;
  }

  const stringValue = String(value);
  const normalizedValue = stringValue.replace(
    /\.(\d{3})\d+(?=Z|[+-]\d{2}:?\d{2}$)/,
    ".$1"
  );
  const timestamp = new Date(normalizedValue).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.NaN;
};

const getXValue = (
  row: Record<string, unknown>,
  rowIndex: number,
  descriptor: XDescriptor,
  dateOrigin = 0
) => {
  if (descriptor.kind === "index" || descriptor.kind === "category") {
    return rowIndex;
  }
  if (descriptor.kind === "date") {
    const timestamp = toTimestamp(row[descriptor.field]);
    return Number.isFinite(timestamp) ? timestamp - dateOrigin : Number.NaN;
  }
  return toFiniteNumber(row[descriptor.field]);
};

const getDateOrigin = (
  rows: Record<string, unknown>[],
  descriptor: XDescriptor
) => {
  if (descriptor.kind !== "date") return undefined;

  let min = Number.POSITIVE_INFINITY;
  rows.forEach((row) => {
    const timestamp = toTimestamp(row[descriptor.field]);
    if (Number.isFinite(timestamp)) {
      min = Math.min(min, timestamp);
    }
  });

  return Number.isFinite(min) ? min : undefined;
};

const formatNumber = (value: number, precision?: number) => {
  if (!Number.isFinite(value)) return "—";

  const options =
    precision === undefined
      ? { maximumFractionDigits: 6 }
      : { minimumFractionDigits: precision, maximumFractionDigits: precision };

  return new Intl.NumberFormat("ru-RU", options).format(value);
};

const resolvePrecision = (
  precisions: Record<string, number> | undefined,
  mnemonic?: string
) => (mnemonic ? precisions?.[mnemonic.trim().toLowerCase()] : undefined);

const hasProgressiveX = (
  rows: Record<string, unknown>[],
  descriptor: XDescriptor
) => {
  if (descriptor.kind === "index" || descriptor.kind === "category")
    return true;
  let previous = Number.NEGATIVE_INFINITY;
  return rows.every((row, index) => {
    const value = getXValue(row, index, descriptor);
    if (!Number.isFinite(value) || value < previous) return false;
    previous = value;
    return true;
  });
};

const resolveXInterval = (values: Float32Array | Float64Array) => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (!Number.isFinite(value)) continue;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;

  return { start: min, end: max };
};

const resolveRowsMinMaxInterval = (
  rows: Record<string, unknown>[],
  startIndex: number,
  endIndex: number,
  mnemonics: string[]
) => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (let rowIndex = startIndex; rowIndex < endIndex; rowIndex += 1) {
    const row = rows[rowIndex];
    mnemonics.forEach((mnemonic) => {
      const value = toFiniteNumber(row[mnemonic]);
      if (!Number.isFinite(value)) return;
      min = Math.min(min, value);
      max = Math.max(max, value);
    });
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;

  return { start: min, end: max };
};

const overlayGroupKeyFor = (unit: string, mnemonic: string) =>
  unit || `__overlay_${mnemonic}`;

const resolveOverlayInterval = (
  points: OverlayPoint[],
  pick: (point: OverlayPoint) => number
) => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  points.forEach((point) => {
    const value = pick(point);
    if (!Number.isFinite(value)) return;
    min = Math.min(min, value);
    max = Math.max(max, value);
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;

  return { start: min, end: max };
};

const toDisplayInterval = (
  interval: { start: number; end: number },
  singleValuePadding = Math.abs(interval.start || 1) * 0.05 || 1
) => {
  if (interval.start !== interval.end) return interval;

  return {
    start: interval.start - singleValuePadding,
    end: interval.end + singleValuePadding,
  };
};

const toPaddedDisplayInterval = (
  interval: { start: number; end: number },
  paddingRatio = 0.05
) => {
  if (interval.start === interval.end) return toDisplayInterval(interval);

  const min = Math.min(interval.start, interval.end);
  const max = Math.max(interval.start, interval.end);
  const padding = Math.abs(max - min) * paddingRatio;
  const padded = {
    start: min - padding,
    end: max + padding,
  };

  return interval.start <= interval.end
    ? padded
    : { start: padded.end, end: padded.start };
};

const toYAxisDisplayInterval = (
  interval: { start: number; end: number },
  paddingRatio = 0.0
) => {
  if (interval.start === interval.end) {
    const padding = Math.abs(interval.start || 1) * paddingRatio || 1;
    return interval.start <= interval.end
      ? { start: interval.start, end: interval.end + padding }
      : { start: interval.start, end: interval.end - padding };
  }

  const min = Math.min(interval.start, interval.end);
  const max = Math.max(interval.start, interval.end);
  const padding = Math.abs(max - min) * paddingRatio;
  const padded = {
    start: min,
    end: max + padding,
  };

  return interval.start <= interval.end
    ? padded
    : { start: padded.end, end: padded.start };
};

const getNiceStep = (rawStep: number) => {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;

  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const niceNormalized =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;

  return niceNormalized * magnitude;
};

const alignIntervalToTickGrid = (
  interval: { start: number; end: number },
  segmentCount = INITIAL_Y_TICK_SEGMENTS
) => {
  const isAscending = interval.start <= interval.end;
  const start = interval.start;
  const end = interval.end;
  const range = Math.abs(end - start);
  const step = getNiceStep(range / segmentCount);
  const alignedEnd = isAscending
    ? start + step * segmentCount
    : start - step * segmentCount;

  return { start, end: alignedEnd };
};

const alignIntervalsToSharedTickGrid = <
  T extends { displayInterval: { start: number; end: number } }
>(
  items: T[]
) =>
  items.map((item) => ({
    ...item,
    displayInterval: alignIntervalToTickGrid(item.displayInterval),
  }));

const getSingleXValuePadding = (descriptor: XDescriptor) => {
  if (descriptor.kind === "date") return 60 * 1000;
  return 1;
};

const mergeInterval = (
  current: { start: number; end: number } | undefined,
  incoming: { start: number; end: number }
) => {
  if (!current) return incoming;

  return {
    start: Math.min(current.start, incoming.start),
    end: Math.max(current.end, incoming.end),
  };
};

const zoomIntervalAround = (
  interval: { start: number; end: number },
  anchor: number,
  factor: number
) => {
  const startDistance = anchor - interval.start;
  const endDistance = interval.end - anchor;

  return {
    start: anchor - startDistance * factor,
    end: anchor + endDistance * factor,
  };
};

const isFiniteInterval = (
  value: unknown
): value is { start: number; end: number } =>
  Boolean(
    value &&
      typeof value === "object" &&
      Number.isFinite((value as { start?: unknown }).start) &&
      Number.isFinite((value as { end?: unknown }).end)
  );

const isChartContainerReady = (container: HTMLDivElement) => {
  const rect = container.getBoundingClientRect();
  return (
    rect.width > 1 &&
    rect.height > 1 &&
    container.clientWidth > 1 &&
    container.clientHeight > 1
  );
};

const restoreAxisDefaultInterval = (axis: Axis) => {
  const defaultInterval = axis.getDefaultInterval();
  if (!isFiniteInterval(defaultInterval)) {
    axis.fit(false, false);
    return;
  }

  axis.setInterval({
    start: defaultInterval.start,
    end: defaultInterval.end,
    stopAxisAfter: true,
  });
};

const getDateTimeParts = (value: number) => {
  const date = new Date(value);

  return {
    datePart: new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date),
    timePart: new Intl.DateTimeFormat("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date),
  };
};

const formatTooltipDateTime = (value: number) => {
  const { datePart, timePart } = getDateTimeParts(value);

  return `${datePart} ${timePart}`;
};

const toAbsoluteDateTimeValue = (value: number, dateOrigin?: number) => {
  if (dateOrigin === undefined) return value;

  // LCJS DateTime cursor formatters may receive either an absolute timestamp
  // or a value relative to dateOrigin, depending on the tick strategy state.
  // Values smaller than year 2000 are treated as relative offsets.
  return Math.abs(value) < 946684800000 ? value + dateOrigin : value;
};

const applyAxisVisualStyle = (axis: Axis, colors: ChartThemeColors) => {
  axis
    .setStrokeStyle(
      new SolidLine({
        thickness: 1,
        fillStyle: new SolidFill({ color: colors.axisLine }),
      })
    )
    .setTitleFillStyle(new SolidFill({ color: colors.axisTitle }));
};

const applyDateTimeTickStrategy = (
  axis: Axis,
  dateOrigin: number | undefined,
  colors: ChartThemeColors
) => {
  axis.setTickStrategy(AxisTickStrategies.DateTime, (ticks) => {
    const styledTicks = ticks
      .setMajorTickStyle((major) =>
        major
          .setLabelFont((font) => font.setSize(11))
          .setLabelFillStyle(new SolidFill({ color: colors.majorLabel }))
          .setTickLength(4)
          .setTickPadding(3)
          .setGridStrokeStyle(
            new SolidLine({
              thickness: 1,
              fillStyle: new SolidFill({ color: colors.majorGrid }),
            })
          )
      )
      .setMinorTickStyle((minor) =>
        minor
          .setLabelFillStyle(new SolidFill({ color: colors.minorLabel }))
          .setLabelFont((font) => font.setSize(10))
          .setTickLength(2)
          .setTickPadding(2)
          .setGridStrokeStyle(
            new SolidLine({
              thickness: 1,
              fillStyle: new SolidFill({ color: colors.minorGrid }),
            })
          )
      );

    return dateOrigin !== undefined
      ? styledTicks
          .setDateOrigin(new Date(dateOrigin))
          .setCursorFormatter((value) =>
            formatTooltipDateTime(toAbsoluteDateTimeValue(value, dateOrigin))
          )
      : styledTicks.setCursorFormatter((value) =>
          formatTooltipDateTime(toAbsoluteDateTimeValue(value, dateOrigin))
        );
  });
};

const LogChart: React.FC<LogChartProps> = ({
  data,
  title,
  emptyMessage = "Нет данных для отображения",
  indexCurve = "DateTime",
  storageKey,
  overlaySeries,
  depthReferenceLines,
  curvePrecisions,
  autoFitKey,
  hiddenSeriesOverride,
  highlightedSeriesMnemonic,
  onHiddenSeriesChange,
  onClose,
  onOpen,
  isDialog = false,
}) => {
  const appTheme = useTheme();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const runtimeRef = useRef<ChartRuntime>();
  // Read through a ref so late-arriving precisions reach the cursor formatter
  // without recreating the WebGL chart.
  const curvePrecisionsRef = useRef(curvePrecisions);
  curvePrecisionsRef.current = curvePrecisions;
  const hiddenSeriesStorageKey = useMemo(
    () => `log-chart:hidden-series:${storageKey || title}`,
    [storageKey, title]
  );
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>(
    () => readStoredRecord(hiddenSeriesStorageKey)
  );
  const displayedHiddenSeries = hiddenSeriesOverride ?? hiddenSeries;
  const currentHiddenSeriesStorageKeyRef = useRef(hiddenSeriesStorageKey);
  const skipHiddenSeriesPersistRef = useRef(false);
  const currentAutoFitKeyRef = useRef(autoFitKey);
  const pendingAutoFitRef = useRef(false);

  const rows = data?.data || [];
  const specs = data?.curveSpecifications || [];
  const xDescriptor = useMemo(
    () => resolveXDescriptor(rows, specs, indexCurve),
    [rows, specs, indexCurve]
  );
  const measuredDepthDescriptor = useMemo(
    () => resolveMeasuredDepthDescriptor(rows, specs),
    [rows, specs]
  );
  const redrawOnRealtimeUpdate = useMemo(
    () => shouldRedrawChartOnRealtimeUpdate(title),
    [title]
  );
  const measuredDepthMode = Boolean(
    redrawOnRealtimeUpdate && measuredDepthDescriptor
  );
  const axisField = measuredDepthMode
    ? measuredDepthDescriptor?.field || xDescriptor.field
    : xDescriptor.field;
  // Overlays are only meaningful on the measured-depth (calculated) chart, where
  // they share the depth Y axis with the log curves.
  const overlays = useMemo(
    () =>
      measuredDepthMode
        ? (overlaySeries || []).filter((item) => item.points?.length)
        : [],
    [overlaySeries, measuredDepthMode]
  );
  const overlayKey = useMemo(() => JSON.stringify(overlays), [overlays]);
  // Reference lines only make sense on the depth axis of the calculated chart.
  const depthReferenceValues = useMemo(
    () =>
      measuredDepthMode
        ? (depthReferenceLines || []).filter((value) => Number.isFinite(value))
        : [],
    [depthReferenceLines, measuredDepthMode]
  );
  const depthReferenceKey = useMemo(
    () => JSON.stringify(depthReferenceValues),
    [depthReferenceValues]
  );
  const structureKey = useMemo(
    () =>
      JSON.stringify({
        x: xDescriptor,
        y: measuredDepthMode ? measuredDepthDescriptor : undefined,
        measuredDepthMode,
        curves: specs.map(({ mnemonic, unit }) => ({ mnemonic, unit })),
        overlays: overlayKey,
      }),
    [specs, xDescriptor, measuredDepthDescriptor, measuredDepthMode, overlayKey]
  );
  const chartColors = useMemo<ChartThemeColors>(
    () => ({
      background: toThemeColor(appTheme.colors.background),
      axisLine: toThemeColor(appTheme.colors.borderColor),
      axisTitle: toThemeColor(appTheme.colors.textSecondary),
      majorGrid: toThemeColor(appTheme.colors.chartGrid, 220),
      minorGrid: toThemeColor(appTheme.colors.chartGrid, 70),
      majorLabel: toThemeColor(appTheme.colors.textSecondary),
      minorLabel: toThemeColor(appTheme.colors.textSecondary, 180),
      tooltipBackground: toThemeColor(appTheme.colors.background, 245),
      tooltipBorder: toThemeColor(appTheme.colors.borderColor),
      tooltipText: toThemeColor(appTheme.colors.textSecondary),
      tooltipHeaderText: toThemeColor(appTheme.colors.secondary),
      tooltipHeaderBackground: toThemeColor(appTheme.colors.primary_100, 230),
      tooltipTickMarkerBackground: toThemeColor(
        appTheme.colors.background,
        245
      ),
      tooltipTickMarkerBorder: toThemeColor(appTheme.colors.borderColor),
      tooltipTickMarkerText: toThemeColor(appTheme.colors.textSecondary),
      stageLine: toThemeColor(appTheme.colors.textSecondary),
      // Matches the casing color of the 2D well schematic (WellCementing).
      casingLine: toThemeColor(appTheme.colors.iconSecondary),
      stageLabelBackground: toThemeColor(appTheme.colors.background, 245),
      stageLabelBorder: toThemeColor(appTheme.colors.darkGrey),
      stageLabelText: toThemeColor(appTheme.colors.textSecondary),
    }),
    [
      appTheme.colors.background,
      appTheme.colors.borderColor,
      appTheme.colors.textSecondary,
      appTheme.colors.chartGrid,
      appTheme.colors.secondary,
      appTheme.colors.primary_100,
      appTheme.colors.darkGrey,
      appTheme.colors.iconSecondary,
    ]
  );
  const hasData =
    rows.length > 0 &&
    specs.some((spec) => isPlottableCurve(spec, measuredDepthMode, axisField));
  const legendItems = useMemo(() => {
    const curveItems = specs
      .filter((spec) => isPlottableCurve(spec, measuredDepthMode, axisField))
      .map((spec, index) => ({
        mnemonic: spec.mnemonic,
        displayName: getCurveTermTranslation(spec.mnemonic),
        label: normalizeUnit(spec.unit)
          ? `${getCurveTermTranslation(spec.mnemonic)} (${normalizeUnit(
              spec.unit
            )})`
          : getCurveTermTranslation(spec.mnemonic),
        color: LOG_CHART_COLORS[index % LOG_CHART_COLORS.length],
      }));

    // Overlay colors continue the same palette index used when their series are
    // created, so legend markers match the rendered lines.
    const overlayItems = overlays.map((overlay, index) => {
      const overlayUnit = normalizeUnit(overlay.unit);
      const translated = getCurveTermTranslation(overlay.mnemonic);
      return {
        mnemonic: overlay.mnemonic,
        displayName: translated,
        label: overlayUnit ? `${translated} (${overlayUnit})` : translated,
        color:
          LOG_CHART_COLORS[
            (curveItems.length + index) % LOG_CHART_COLORS.length
          ],
      };
    });

    return [...curveItems, ...overlayItems];
  }, [specs, axisField, measuredDepthMode, overlays]);

  useEffect(() => {
    if (currentHiddenSeriesStorageKeyRef.current === hiddenSeriesStorageKey) {
      return;
    }

    currentHiddenSeriesStorageKeyRef.current = hiddenSeriesStorageKey;
    skipHiddenSeriesPersistRef.current = true;
    setHiddenSeries(readStoredRecord(hiddenSeriesStorageKey));
  }, [hiddenSeriesStorageKey]);

  useEffect(() => {
    if (skipHiddenSeriesPersistRef.current) {
      skipHiddenSeriesPersistRef.current = false;
      return;
    }

    window.localStorage.setItem(
      hiddenSeriesStorageKey,
      JSON.stringify(hiddenSeries)
    );
  }, [hiddenSeries, hiddenSeriesStorageKey]);

  useEffect(() => {
    if (currentAutoFitKeyRef.current === autoFitKey) return;

    currentAutoFitKeyRef.current = autoFitKey;
    pendingAutoFitRef.current = true;
    if (runtimeRef.current) {
      runtimeRef.current.userViewLocked = false;
    }
  }, [autoFitKey]);

  useEffect(() => {
    const container = surfaceRef.current;
    if (!container || !hasData) return;

    let handleChartWheelZoom = (_event: WheelEvent) => {};

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey || !isChartContainerReady(container)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (runtimeRef.current) {
        runtimeRef.current.userViewLocked = true;
      }
      try {
        handleChartWheelZoom(event);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (
          (error instanceof DOMException && error.name === "IndexSizeError") ||
          message.includes("getImageData") ||
          message.includes("source width is 0")
        ) {
          return;
        }
        throw error;
      }
    };
    const lockUserView = () => {
      if (runtimeRef.current) {
        runtimeRef.current.userViewLocked = true;
      }
    };

    const lc = lightningChart({
      license: process.env.REACT_APP_LCHART,
    });
    const chartBackgroundFill = new SolidFill({
      color: chartColors.background,
    });
    const chartTheme = {
      ...Themes.light,
      cursorTickMarkerXBackgroundFillStyle: new SolidFill({
        color: chartColors.tooltipTickMarkerBackground,
      }),
      cursorTickMarkerXBackgroundStrokeStyle: new SolidLine({
        thickness: 1,
        fillStyle: new SolidFill({
          color: chartColors.tooltipTickMarkerBorder,
        }),
      }),
      cursorTickMarkerXTextFillStyle: new SolidFill({
        color: chartColors.tooltipTickMarkerText,
      }),
    };
    const chart = lc.ChartXY({
      container,
      theme: chartTheme,
      animationsEnabled: false,
    });
    chart
      .setTitle("")
      .setPadding({ top: 8, right: 42, bottom: 2, left: 8 })
      .setBackgroundFillStyle(chartBackgroundFill)
      .setSeriesBackgroundFillStyle(chartBackgroundFill)
      .setCursorMode(CURSOR_MODE)
      .setCursor((cursor) =>
        cursor
          .setTickMarkerYVisible(false)
          .setGridStrokeYStyle(emptyLine)
          .setTickMarkerX((tickMarker) =>
            tickMarker.setTextFillStyle(
              new SolidFill({ color: chartColors.tooltipTickMarkerText })
            )
          )
          .setResultTable((resultTable) =>
            resultTable
              .setTextFillStyle(
                new SolidFill({ color: chartColors.tooltipText })
              )
              .setBackground((background) =>
                background
                  .setFillStyle(
                    new SolidFill({ color: chartColors.tooltipBackground })
                  )
                  .setStrokeStyle(
                    new SolidLine({
                      thickness: 1,
                      fillStyle: new SolidFill({
                        color: chartColors.tooltipBorder,
                      }),
                    })
                  )
              )
          )
      );

    const xAxis = chart.getDefaultAxisX();
    applyAxisVisualStyle(xAxis, chartColors);
    const xUnit =
      xDescriptor.kind === "date" ? "" : normalizeUnit(xDescriptor.unit);
    const translatedXField = getCurveTermTranslation(xDescriptor.field);
    const xTitle = xUnit
      ? `${translatedXField} (${xUnit})`
      : xDescriptor.field === "__index"
      ? "Индекс"
      : translatedXField;
    xAxis.setTitle(xDescriptor.field === "__index" ? "Индекс" : xTitle);
    const axisTitleByAxis = new Map<Axis, string>();
    axisTitleByAxis.set(
      xAxis,
      xDescriptor.field === "__index" ? "Индекс" : xTitle
    );

    const categoryLabels: string[] = [];
    if (xDescriptor.kind === "date") {
      applyDateTimeTickStrategy(xAxis, undefined, chartColors);
    } else if (xDescriptor.kind === "category") {
      xAxis.setTickStrategy(AxisTickStrategies.Numeric, (ticks) =>
        ticks
          .setFormattingFunction((value) => {
            const index = Math.round(value);
            return categoryLabels[index] ?? "";
          })
          .setMajorTickStyle((major) =>
            major
              .setLabelFont((font) => font.setSize(11))
              .setLabelFillStyle(
                new SolidFill({ color: chartColors.majorLabel })
              )
              .setTickLength(4)
              .setTickPadding(3)
              .setGridStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({ color: chartColors.majorGrid }),
                })
              )
          )
          .setMinorTickStyle((minor) =>
            minor
              .setLabelFillStyle(emptyFill)
              .setTickLength(2)
              .setTickPadding(1)
              .setGridStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({ color: chartColors.minorGrid }),
                })
              )
          )
      );
    } else {
      xAxis.setTickStrategy(AxisTickStrategies.Numeric, (ticks) =>
        ticks
          .setMajorTickStyle((major) =>
            major
              .setLabelFont((font) => font.setSize(11))
              .setLabelFillStyle(
                new SolidFill({ color: chartColors.majorLabel })
              )
              .setTickLength(4)
              .setTickPadding(3)
              .setGridStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({ color: chartColors.majorGrid }),
                })
              )
          )
          .setMinorTickStyle((minor) =>
            minor
              .setLabelFillStyle(emptyFill)
              .setTickLength(2)
              .setTickPadding(1)
              .setGridStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({ color: chartColors.minorGrid }),
                })
              )
          )
      );
    }

    // The first and last log values rarely land on the automatic grid, so they
    // get their own ticks that are pinned to the data ends.
    const formatXEdgeValue = (value: number) => {
      const runtime = runtimeRef.current;
      if (xDescriptor.kind === "category") {
        return (runtime?.categoryLabels ?? [])[Math.round(value)] ?? "";
      }
      return formatNumber(
        value,
        resolvePrecision(curvePrecisionsRef.current, xDescriptor.field)
      );
    };
    const toAbsoluteXTime = (value: number) =>
      value + (runtimeRef.current?.xOrigin ?? 0);
    // Tick labels are single-line, so a date edge label is stacked from two
    // ticks at the same position: the date row and the time row below it.
    const xEdgeRowFormatters: Array<(value: number) => string> =
      xDescriptor.kind === "date"
        ? [
            (value) => getDateTimeParts(toAbsoluteXTime(value)).datePart,
            (value) => getDateTimeParts(toAbsoluteXTime(value)).timePart,
          ]
        : [formatXEdgeValue];
    const createXEdgeTicks = () =>
      xEdgeRowFormatters.map((formatter, row) =>
        xAxis
          .addCustomTick()
          // Only the first row keeps the tick line; a longer line on the rows
          // below would be drawn straight through the row above.
          .setTickLength(row === 0 ? X_EDGE_TICK_LENGTH : 0)
          .setTickLabelPadding(
            row === 0
              ? 3
              : X_EDGE_TICK_LENGTH + 3 + row * X_EDGE_TICK_ROW_HEIGHT
          )
          .setMarker((marker) =>
            marker
              .setTextFont((font) => font.setSize(11).setWeight("bold"))
              .setTextFillStyle(
                new SolidFill({ color: chartColors.majorLabel })
              )
          )
          // The edge gridlines would coincide with the plot borders.
          .setGridStrokeStyle(emptyLine)
          .setPointerEvents(false)
          .setTextFormatter(formatter)
          .setVisible(false)
      );
    const xEdgeTicks = measuredDepthMode
      ? undefined
      : { start: createXEdgeTicks(), end: createXEdgeTicks() };
    if (xEdgeTicks) {
      // Reserve room for the extra label row so it is not cut off.
      xAxis.setThickness({ min: X_EDGE_TICK_AXIS_THICKNESS });
    }

    const yAxis = chart.getDefaultAxisY();
    applyAxisVisualStyle(yAxis, chartColors);
    const depthDescriptor = measuredDepthDescriptor || xDescriptor;
    if (measuredDepthMode) {
      const depthUnit = normalizeUnit(depthDescriptor.unit);
      const depthName = getCurveTermTranslation(depthDescriptor.field);
      const depthTitle = depthUnit ? `${depthName} (${depthUnit})` : depthName;
      yAxis.setTitle(depthTitle);
      axisTitleByAxis.set(yAxis, depthTitle);
      yAxis.setThickness(Y_AXIS_THICKNESS);
      yAxis.setTickStrategy(AxisTickStrategies.Numeric, (ticks) =>
        ticks
          .setMajorTickStyle((major) =>
            major
              .setLabelFont((font) => font.setSize(11))
              .setLabelFillStyle(
                new SolidFill({ color: chartColors.majorLabel })
              )
              .setTickLength(4)
              .setTickPadding(3)
              .setGridStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({ color: chartColors.majorGrid }),
                })
              )
          )
          .setMinorTickStyle((minor) =>
            minor
              .setLabelFillStyle(emptyFill)
              .setTickLength(2)
              .setTickPadding(1)
              .setGridStrokeStyle(
                new SolidLine({
                  thickness: 1,
                  fillStyle: new SolidFill({ color: chartColors.minorGrid }),
                })
              )
          )
      );
      if (depthUnit) yAxis.setUnits(depthUnit, { displayOnAxis: false });
    }

    // Stage boundaries live on their own axis on the opposite side of the index
    // axis — top for time charts, right for the measured-depth one — so the
    // labels sit above the plot instead of colliding with the axis ticks. It
    // carries no ticks of its own and only mirrors the index axis interval.
    const stageAxis = measuredDepthMode
      ? chart.addAxisY({ opposite: true })
      : chart.addAxisX({ opposite: true });
    stageAxis
      .setTitle("")
      .setTickStrategy(AxisTickStrategies.Empty)
      .setStrokeStyle(emptyLine)
      .setUserInteractions(undefined)
      // Room for one row of stage labels.
      .setThickness(STAGE_LABEL_AXIS_THICKNESS);
    const stageAxisSync = synchronizeAxisIntervals(
      measuredDepthMode ? yAxis : xAxis,
      stageAxis
    );
    const stageStroke = new SolidLine({
      thickness: 1,
      fillStyle: new SolidFill({ color: chartColors.stageLine }),
    });
    const stageLabelFill = new SolidFill({
      color: chartColors.stageLabelBackground,
    });
    const stageLabelStroke = new SolidLine({
      thickness: 1,
      fillStyle: new SolidFill({ color: chartColors.stageLabelBorder }),
    });
    const stageLabelTextFill = new SolidFill({
      color: chartColors.stageLabelText,
    });
    const stageTooltip = document.createElement("div");
    Object.assign(stageTooltip.style, {
      position: "absolute",
      display: "none",
      zIndex: "10",
      pointerEvents: "none",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "600",
      whiteSpace: "nowrap",
      color: appTheme.colors.textSecondary,
      background: appTheme.colors.background,
      border: `1px solid ${appTheme.colors.borderColor}`,
      boxShadow: "none",
    });
    container.appendChild(stageTooltip);
    // const axisTooltip = document.createElement("div");
    // Object.assign(axisTooltip.style, {
    //   position: "absolute",
    //   display: "none",
    //   zIndex: "11",
    //   pointerEvents: "none",
    //   padding: "4px 8px",
    //   borderRadius: "4px",
    //   fontSize: "12px",
    //   fontWeight: "600",
    //   whiteSpace: "nowrap",
    //   color: appTheme.colors.textSecondary,
    //   background: appTheme.colors.background,
    //   border: `1px solid ${appTheme.colors.borderColor}`,
    //   boxShadow: "none",
    // });
    // container.appendChild(axisTooltip);
    const formatStageAxisValue = (value: number) => {
      if (measuredDepthMode) {
        const unit = normalizeUnit(depthDescriptor.unit);
        const formatted = formatNumber(
          value,
          resolvePrecision(curvePrecisionsRef.current, depthDescriptor.field)
        );
        return unit ? `${formatted} ${unit}` : formatted;
      }
      if (xDescriptor.kind === "date") {
        return formatTooltipDateTime(
          value + (runtimeRef.current?.xOrigin ?? 0)
        );
      }
      if (xDescriptor.kind === "category") {
        return runtimeRef.current?.categoryLabels[Math.round(value)] ?? "";
      }
      const formatted = formatNumber(
        value,
        resolvePrecision(curvePrecisionsRef.current, xDescriptor.field)
      );
      const unit = normalizeUnit(xDescriptor.unit);
      return unit ? `${formatted} ${unit}` : formatted;
    };
    const showStageTooltip = (event: PointerEvent, value: number) => {
      const rect = container.getBoundingClientRect();
      stageTooltip.textContent = formatStageAxisValue(value);
      stageTooltip.style.left = `${event.clientX - rect.left + 10}px`;
      stageTooltip.style.top = `${event.clientY - rect.top + 10}px`;
      stageTooltip.style.display = "block";
    };
    const hideStageTooltip = () => {
      stageTooltip.style.display = "none";
    };
    const addStageMarker = (value: number, label: string): StageMarker => {
      const line = stageAxis
        .addConstantLine()
        .setValue(value)
        .setStrokeStyle(stageStroke)
        .setEffect(false)
        .setUserInteractions(undefined);
      const tick = stageAxis
        .addCustomTick(UIElementBuilders.PointableTextBox)
        .setValue(value)
        .setMarker((marker) => {
          const pointableMarker = marker as unknown as UIPointableTextBox;
          return pointableMarker
            .setTextFillStyle(stageLabelTextFill)
            .setBackground((background) =>
              background
                .setFillStyle(stageLabelFill)
                .setStrokeStyle(stageLabelStroke)
            );
        })
        .setTextFormatter(() => label)
        // The constant line already draws the boundary.
        .setGridStrokeLength(0)
        .setPointerEvents(true);

      tick.addEventListener("pointerenter", (event) => {
        showStageTooltip(event, value);
      });
      tick.addEventListener("pointermove", (event) => {
        showStageTooltip(event, value);
      });
      tick.addEventListener("pointerleave", hideStageTooltip);

      return { line, tick };
    };

    const curveGroupKeyFor = (spec: CurveSpecification) => {
      const unit = normalizeUnit(spec.unit);
      return measuredDepthMode
        ? unit || `__${spec.mnemonic}`
        : `__${spec.mnemonic}`;
    };
    const overlayAxisGroupKeyFor = (overlay: LogChartOverlaySeries) =>
      measuredDepthMode
        ? overlayGroupKeyFor(normalizeUnit(overlay.unit), overlay.mnemonic)
        : `__overlay_${overlay.mnemonic}`;

    const groups = new Map<string, CurveSpecification[]>();
    specs
      .filter((spec) => isPlottableCurve(spec, measuredDepthMode, axisField))
      .forEach((spec) => {
        // In regular time/index charts each curve gets its own Y axis. In
        // measured-depth charts the horizontal axes keep the existing grouping
        // by unit, because the depth axis is the only Y axis there.
        const key = curveGroupKeyFor(spec);
        groups.set(key, [...(groups.get(key) || []), spec]);
      });

    // Overlays follow the same rule: grouped by unit only in measured-depth
    // charts, isolated otherwise.
    overlays.forEach((overlay) => {
      const key = overlayAxisGroupKeyFor(overlay);
      if (!groups.has(key)) groups.set(key, []);
    });

    const axisTitleColorByGroup = new Map<string, string>();
    let nextLegendColorIndex = 0;
    groups.forEach((groupSpecs, groupKey) => {
      if (groupSpecs.length > 0) {
        axisTitleColorByGroup.set(
          groupKey,
          LOG_CHART_COLORS[nextLegendColorIndex % LOG_CHART_COLORS.length]
        );
      }
      nextLegendColorIndex += groupSpecs.length;
    });
    overlays.forEach((overlay) => {
      const groupKey = overlayAxisGroupKeyFor(overlay);
      if (!axisTitleColorByGroup.has(groupKey)) {
        axisTitleColorByGroup.set(
          groupKey,
          LOG_CHART_COLORS[nextLegendColorIndex % LOG_CHART_COLORS.length]
        );
      }
      nextLegendColorIndex += 1;
    });

    const axesByGroup = new Map<string, Axis>();
    const xAxes: ChartRuntime["xAxes"] = [];
    const yAxes: ChartRuntime["yAxes"] = [];
    Array.from(groups.entries()).forEach(([groupKey, groupSpecs], index) => {
      const axis = measuredDepthMode
        ? index === 0
          ? xAxis
          : chart.addAxisX({ opposite: index % 2 === 1 })
        : index === 0
        ? yAxis
        : chart.addAxisY({ opposite: false });
      applyAxisVisualStyle(axis, chartColors);
      const axisTitleColor = axisTitleColorByGroup.get(groupKey);
      const axisLegendColor =
        !measuredDepthMode && axisTitleColor
          ? ColorHEX(axisTitleColor)
          : undefined;
      if (axisLegendColor) {
        axis.setStrokeStyle(
          new SolidLine({
            thickness: 1,
            fillStyle: new SolidFill({ color: axisLegendColor }),
          })
        );
        // .setTitleFillStyle(new SolidFill({ color: axisLegendColor }))
        // .setTitleFont((font) => font.setWeight(500));
      }
      const overlayForGroup = overlays.find(
        (overlay) => overlayAxisGroupKeyFor(overlay) === groupKey
      );
      const unit =
        groupSpecs.length > 0
          ? normalizeUnit(groupSpecs[0].unit)
          : normalizeUnit(overlayForGroup?.unit);
      const curveNames = groupSpecs.map((spec) =>
        getCurveTermTranslation(spec.mnemonic)
      );
      const axisNames = (
        curveNames.length
          ? curveNames
          : overlays
              .filter((overlay) => overlayAxisGroupKeyFor(overlay) === groupKey)
              .map((overlay) => getCurveTermTranslation(overlay.mnemonic))
      ).join(", ");
      const axisTitle = unit ? `${axisNames} (${unit})` : axisNames;
      if (axisTitle) axisTitleByAxis.set(axis, axisTitle);
      axis.setThickness(Y_AXIS_THICKNESS);
      const shouldShowGridLines = measuredDepthMode || index === 0;
      axis.setTickStrategy(AxisTickStrategies.Numeric, (ticks) =>
        ticks
          .setMajorTickStyle((major) =>
            major
              .setLabelFont((font) => font.setSize(11))
              .setLabelFillStyle(
                new SolidFill({
                  color: axisLegendColor ?? chartColors.majorLabel,
                })
              )
              .setTickLength(4)
              .setTickPadding(3)
              .setGridStrokeStyle(
                shouldShowGridLines
                  ? new SolidLine({
                      thickness: 1,
                      fillStyle: new SolidFill({
                        color: chartColors.majorGrid,
                      }),
                    })
                  : emptyLine
              )
          )
          .setMinorTickStyle((minor) =>
            minor
              .setLabelFillStyle(emptyFill)
              .setTickLength(2)
              .setTickPadding(1)
              .setGridStrokeStyle(
                shouldShowGridLines
                  ? new SolidLine({
                      thickness: 1,
                      fillStyle: new SolidFill({
                        color: chartColors.minorGrid,
                      }),
                    })
                  : emptyLine
              )
          )
      );
      if (unit) axis.setUnits(unit, { displayOnAxis: false });
      axesByGroup.set(groupKey, axis);
      const axisRuntimeItem = {
        groupKey,
        axis,
        mnemonics: groupSpecs.map((spec) => spec.mnemonic),
      };
      if (measuredDepthMode) {
        xAxes.push({
          ...axisRuntimeItem,
          side: index % 2 === 1 ? "top" : "bottom",
        });
      } else {
        yAxes.push({
          ...axisRuntimeItem,
          side: "left",
        });
      }
    });

    const bindings: SeriesBinding[] = [];
    const dataPattern =
      !measuredDepthMode && hasProgressiveX(rows, xDescriptor)
        ? "ProgressiveX"
        : null;
    let colorIndex = 0;
    groups.forEach((groupSpecs, groupKey) => {
      const seriesXAxis = measuredDepthMode ? axesByGroup.get(groupKey) : xAxis;
      const seriesYAxis = measuredDepthMode ? yAxis : axesByGroup.get(groupKey);
      if (!seriesXAxis || !seriesYAxis) return;

      groupSpecs.forEach((spec) => {
        const colorHex = LOG_CHART_COLORS[colorIndex % LOG_CHART_COLORS.length];
        const color = ColorHEX(colorHex);
        const unit = normalizeUnit(spec.unit);
        const translatedMnemonic = getCurveTermTranslation(spec.mnemonic);
        const label = unit
          ? `${translatedMnemonic} (${unit})`
          : translatedMnemonic;
        const maxSampleCount = Math.max(
          Math.min(rows.length + REALTIME_SAMPLE_HEADROOM, REALTIME_SAMPLE_MAX),
          rows.length || REALTIME_SAMPLE_HEADROOM
        );
        const series = chart
          .addPointLineAreaSeries({
            xAxis: seriesXAxis,
            yAxis: seriesYAxis,
            automaticColorIndex: colorIndex,
            dataPattern,
            dataStorage: Float32Array,
          })
          .setMaxSampleCount(maxSampleCount)
          .setName(translatedMnemonic)
          .setAreaFillStyle(emptyFill)
          .setPointFillStyle(emptyFill)
          .setPointSize(0)
          .setStrokeStyle(
            new SolidLine({
              thickness: NORMAL_LINE_WIDTH,
              fillStyle: new SolidFill({ color }),
            })
          )
          .setEffect(false)
          .setVisible(!displayedHiddenSeries[spec.mnemonic]);

        bindings.push({
          mnemonic: spec.mnemonic,
          label,
          color: colorHex,
          unit,
          series,
        });
        colorIndex += 1;
      });
    });

    const overlayBindings: OverlayBinding[] = [];
    overlays.forEach((overlay) => {
      const groupKey = overlayAxisGroupKeyFor(overlay);
      const seriesXAxis = axesByGroup.get(groupKey);
      if (!seriesXAxis) return;

      const colorHex = LOG_CHART_COLORS[colorIndex % LOG_CHART_COLORS.length];
      const color = ColorHEX(colorHex);
      const unit = normalizeUnit(overlay.unit);
      const translatedMnemonic = getCurveTermTranslation(overlay.mnemonic);
      const label = unit
        ? `${translatedMnemonic} (${unit})`
        : translatedMnemonic;
      const series = chart
        .addPointLineAreaSeries({
          xAxis: seriesXAxis,
          yAxis,
          automaticColorIndex: colorIndex,
          dataStorage: Float32Array,
        })
        .setMaxSampleCount(Math.max(overlay.points.length, 16))
        .setName(translatedMnemonic)
        .setAreaFillStyle(emptyFill)
        // No point markers, so overlays match the plain line style of the log
        // curves.
        .setPointFillStyle(emptyFill)
        .setPointSize(0)
        .setStrokeStyle(
          new SolidLine({
            thickness: NORMAL_LINE_WIDTH,
            fillStyle: new SolidFill({ color }),
          })
        )
        .setEffect(false)
        .setVisible(!displayedHiddenSeries[overlay.mnemonic]);

      overlayBindings.push({
        mnemonic: overlay.mnemonic,
        label,
        color: colorHex,
        unit,
        groupKey,
        points: overlay.points,
        series,
      });
      colorIndex += 1;
    });

    const bindingBySeries = new Map<unknown, SeriesBinding | OverlayBinding>();
    bindings.forEach((binding) => bindingBySeries.set(binding.series, binding));
    overlayBindings.forEach((binding) =>
      bindingBySeries.set(binding.series, binding)
    );

    chart.setCursorFormatting((_chart, hit, hits) => {
      const runtime = runtimeRef.current;
      const precisions = curvePrecisionsRef.current;
      let headerValueText: string;
      if (measuredDepthMode) {
        const depthUnit = normalizeUnit(depthDescriptor.unit);
        const rawValue = formatNumber(
          hit.y,
          resolvePrecision(precisions, depthDescriptor.field)
        );
        headerValueText = depthUnit ? `${rawValue} ${depthUnit}` : rawValue;
      } else if (xDescriptor.kind === "date") {
        headerValueText = formatTooltipDateTime(
          hit.x + (runtime?.xOrigin ?? 0)
        );
      } else {
        let rawValue: string;
        if (xDescriptor.kind === "category") {
          const labels = runtime?.categoryLabels ?? [];
          rawValue = labels[Math.round(hit.x)] ?? String(hit.x);
        } else {
          rawValue = formatNumber(
            hit.x,
            resolvePrecision(precisions, xDescriptor.field)
          );
        }
        const xUnit = normalizeUnit(xDescriptor.unit);
        headerValueText = xUnit ? `${rawValue} ${xUnit}` : rawValue;
      }

      return [
        [
          {
            text: headerValueText,
            font: { weight: "bold" as const },
            fillStyle: new SolidFill({
              color: chartColors.tooltipHeaderText,
            }),
            rowFillStyle: new SolidFill({
              color: chartColors.tooltipHeaderBackground,
            }),
          },
        ],
        ...hits.map((item) => {
          const binding = bindingBySeries.get(item.series);
          const value = measuredDepthMode ? item.x : item.y;
          const formattedValue = formatNumber(
            value,
            resolvePrecision(precisions, binding?.mnemonic)
          );
          const valueText = binding?.unit
            ? `${formattedValue} ${binding.unit}`
            : formattedValue;

          return [
            item.series,
            "",
            {
              text: valueText,
              font: { weight: "bold" as const },
              fillStyle: new SolidFill({ color: chartColors.tooltipText }),
            },
          ];
        }),
      ];
    });

    // Dashed depth reference lines (casing shoe) with a title floating just
    // above each line. The line sits on the depth (Y) axis; the title is a UI
    // text label positioned in plot coordinates so neither axis can clip it.
    const casingStroke = new DashedLine({
      thickness: NORMAL_LINE_WIDTH,
      fillStyle: new SolidFill({ color: chartColors.casingLine }),
      pattern: StipplePatterns.Dashed,
    });
    const casingLabelTextFill = new SolidFill({
      color: chartColors.casingLine,
    });
    let depthReferenceLines: Array<{
      line: ConstantLine;
      label: UIElement;
      value: number;
    }> = [];
    // The title is centered horizontally over the line, so it follows
    // horizontal pans/zooms of the depth chart's density axis.
    const repositionDepthReferenceLabels = () => {
      const { start, end } = xAxis.getInterval();
      const center = (start + end) / 2;
      depthReferenceLines.forEach(({ label, value }) => {
        label.setPosition({ x: center, y: value });
      });
    };
    const syncDepthReferenceLines = (
      items: Array<{ value: number; label: string }>
    ) => {
      depthReferenceLines.forEach(({ line, label }) => {
        line.dispose();
        label.dispose();
      });
      depthReferenceLines = [];
      if (!measuredDepthMode) return;
      const { start, end } = xAxis.getInterval();
      const center = (start + end) / 2;
      items.forEach(({ value, label: text }) => {
        const line = yAxis
          .addConstantLine()
          .setValue(value)
          .setStrokeStyle(casingStroke)
          .setEffect(false)
          .setUserInteractions(undefined);
        const label = chart
          .addUIElement(UIElementBuilders.TextBox, { x: xAxis, y: yAxis })
          // Centered above the line by its bottom-center, lifted a few pixels
          // clear of the line. Plain text — no background box — and locked in
          // place so the user cannot drag it around.
          .setOrigin(UIOrigins.CenterBottom)
          .setDraggingMode(UIDraggingModes.notDraggable)
          .setMargin({ bottom: 4 })
          .setPosition({ x: center, y: value })
          .setText(text)
          .setTextFont((font) => font.setSize(11))
          .setTextFillStyle(casingLabelTextFill)
          .setBackground((background) =>
            background.setFillStyle(emptyFill).setStrokeStyle(emptyLine)
          );
        depthReferenceLines.push({ line, label, value });
      });
    };
    xAxis.addEventListener("intervalchange", repositionDepthReferenceLabels);

    runtimeRef.current = {
      series: bindings,
      overlays: overlayBindings,
      stageField: resolveStageField(rows, specs),
      stageMarkers: [],
      addStageMarker,
      syncedLength: 0,
      categoryLabels,
      xAxis,
      syncDepthReferenceLines,
      userViewLocked: false,
      measuredDepthMode,
      xEdgeTicks,
      xAxes,
      xRanges: new Map(),
      yAxis,
      yDescriptor: depthDescriptor,
      yAxes,
      yRanges: new Map(),
    };

    const xAxisInteractionItems: Array<{
      axis: Axis;
      side: "bottom" | "top";
    }> = measuredDepthMode ? xAxes : [{ axis: xAxis, side: "bottom" as const }];
    const yAxisInteractionItems: Array<{
      axis: Axis;
      side: "left" | "right";
    }> = measuredDepthMode ? [{ axis: yAxis, side: "left" as const }] : yAxes;
    const allInteractionAxes = [
      ...xAxisInteractionItems.map(({ axis }) => axis),
      ...yAxisInteractionItems.map(({ axis }) => axis),
    ];

    const getAxisInteractionTarget = (
      event: MouseEvent | WheelEvent,
      options?: { plotAsX?: boolean }
    ):
      | { kind: "x"; axes: Axis[] }
      | { kind: "y"; axes: Axis[] }
      | { kind: "plot" } => {
      const rect = container.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const pointerY = event.clientY - rect.top;
      const leftAxes = yAxisInteractionItems.filter(
        ({ side }) => side === "left"
      );
      const rightAxes = yAxisInteractionItems.filter(
        ({ side }) => side === "right"
      );
      const topAxes = xAxisInteractionItems.filter(
        ({ side }) => side === "top"
      );
      const bottomAxes = xAxisInteractionItems.filter(
        ({ side }) => side === "bottom"
      );

      const leftBandWidth = leftAxes.length * AXIS_INTERACTION_THICKNESS;
      if (leftAxes.length > 0 && pointerX <= leftBandWidth) {
        return {
          kind: "y",
          axes: yAxisInteractionItems.map(({ axis }) => axis),
        };
      }

      const rightBandWidth = rightAxes.length * AXIS_INTERACTION_THICKNESS;
      if (rightAxes.length > 0 && pointerX >= rect.width - rightBandWidth) {
        return {
          kind: "y",
          axes: yAxisInteractionItems.map(({ axis }) => axis),
        };
      }

      if (
        topAxes.length > 0 &&
        pointerY <= topAxes.length * AXIS_INTERACTION_THICKNESS
      ) {
        return { kind: "x", axes: topAxes.map(({ axis }) => axis) };
      }

      if (
        bottomAxes.length > 0 &&
        pointerY >= rect.height - bottomAxes.length * AXIS_INTERACTION_THICKNESS
      ) {
        return { kind: "x", axes: bottomAxes.map(({ axis }) => axis) };
      }

      return options?.plotAsX
        ? { kind: "x", axes: xAxisInteractionItems.map(({ axis }) => axis) }
        : { kind: "plot" };
    };

    // const getAxisHoverTitle = (event: MouseEvent) => {
    //   const rect = container.getBoundingClientRect();
    //   const pointerX = event.clientX - rect.left;
    //   const pointerY = event.clientY - rect.top;
    //   const leftAxes = yAxisInteractionItems.filter(
    //     ({ side }) => side === "left"
    //   );
    //   const rightAxes = yAxisInteractionItems.filter(
    //     ({ side }) => side === "right"
    //   );
    //   const topAxes = xAxisInteractionItems.filter(({ side }) => side === "top");
    //   const bottomAxes = xAxisInteractionItems.filter(
    //     ({ side }) => side === "bottom"
    //   );

    //   const leftBandWidth = leftAxes.length * AXIS_INTERACTION_THICKNESS;
    //   if (leftAxes.length > 0 && pointerX <= leftBandWidth) {
    //     const axisIndex = Math.min(
    //       Math.floor((leftBandWidth - pointerX) / AXIS_INTERACTION_THICKNESS),
    //       leftAxes.length - 1
    //     );
    //     return axisTitleByAxis.get(leftAxes[axisIndex].axis);
    //   }

    //   const rightBandWidth = rightAxes.length * AXIS_INTERACTION_THICKNESS;
    //   if (rightAxes.length > 0 && pointerX >= rect.width - rightBandWidth) {
    //     const axisIndex = Math.min(
    //       Math.floor(
    //         (pointerX - (rect.width - rightBandWidth)) /
    //           AXIS_INTERACTION_THICKNESS
    //       ),
    //       rightAxes.length - 1
    //     );
    //     return axisTitleByAxis.get(rightAxes[axisIndex].axis);
    //   }

    //   if (
    //     topAxes.length > 0 &&
    //     pointerY <= topAxes.length * AXIS_INTERACTION_THICKNESS
    //   ) {
    //     return topAxes
    //       .map(({ axis }) => axisTitleByAxis.get(axis))
    //       .filter(Boolean)
    //       .join(", ");
    //   }

    //   if (
    //     bottomAxes.length > 0 &&
    //     pointerY >= rect.height - bottomAxes.length * AXIS_INTERACTION_THICKNESS
    //   ) {
    //     return bottomAxes
    //       .map(({ axis }) => axisTitleByAxis.get(axis))
    //       .filter(Boolean)
    //       .join(", ");
    //   }

    //   return undefined;
    // };

    const hideAxisTooltip = () => {
      // axisTooltip.style.display = "none";
    };
    const showAxisTooltip = (event: MouseEvent) => {
      // const title = getAxisHoverTitle(event);
      if (!title) {
        hideAxisTooltip();
        return;
      }
      // axisTooltip.textContent = title;
      // axisTooltip.style.left = `${event.clientX - rect.left + 10}px`;
      // axisTooltip.style.top = `${event.clientY - rect.top + 10}px`;
      // axisTooltip.style.display = "block";
    };

    const zoomAxis = (axis: Axis, anchor: number, zoomFactor: number) => {
      const interval = axis.getInterval();
      const safeAnchor = Number.isFinite(anchor)
        ? anchor
        : (interval.start + interval.end) / 2;
      axis.setInterval({
        ...zoomIntervalAround(interval, safeAnchor, zoomFactor),
        stopAxisAfter: true,
      });
    };

    handleChartWheelZoom = (event: WheelEvent) => {
      if (!isChartContainerReady(container) || event.deltaY === 0) return;

      const zoomFactor = Math.exp(Math.sign(event.deltaY) * 0.18);
      const target = getAxisInteractionTarget(event, { plotAsX: true });

      if (target.kind === "y") {
        target.axes.forEach((axis) => {
          const axisLocation = chart.translateCoordinate(event, {
            x: xAxis,
            y: axis,
          });
          zoomAxis(axis, axisLocation.y, zoomFactor);
        });
        return;
      }
      if (target.kind !== "x") return;

      const chartLocation = chart.translateCoordinate(event, chart.coordsAxis);
      target.axes.forEach((axis) => {
        const axisLocation =
          axis === xAxis
            ? chartLocation
            : chart.translateCoordinate(event, {
                x: axis,
                y: yAxis,
              });
        zoomAxis(axis, axisLocation.x, zoomFactor);
      });
    };

    container.addEventListener("wheel", handleWheel, {
      passive: false,
      capture: true,
    });
    // container.addEventListener("mousemove", showAxisTooltip);
    // container.addEventListener("mouseleave", hideAxisTooltip);
    container.addEventListener("pointerdown", lockUserView);

    const restoreAllAxes = () => {
      if (runtimeRef.current) {
        runtimeRef.current.userViewLocked = false;
      }
      allInteractionAxes.forEach(restoreAxisDefaultInterval);
      if (measuredDepthMode) {
        // Depth grows downward: keep the smallest depth on top after a reset.
        const interval = yAxis.getInterval();
        yAxis.setInterval({
          start: Math.max(interval.start, interval.end),
          end: Math.min(interval.start, interval.end),
          stopAxisAfter: true,
        });
      }
    };
    const restoreInitialView = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (runtimeRef.current) {
        runtimeRef.current.userViewLocked = false;
      }

      const target = getAxisInteractionTarget(event);
      if (target.kind === "y") {
        target.axes.forEach(restoreAxisDefaultInterval);
        if (measuredDepthMode && target.axes.includes(yAxis)) {
          const interval = yAxis.getInterval();
          yAxis.setInterval({
            start: Math.max(interval.start, interval.end),
            end: Math.min(interval.start, interval.end),
            stopAxisAfter: true,
          });
        }
        return;
      }
      if (target.kind === "x") {
        target.axes.forEach(restoreAxisDefaultInterval);
        return;
      }

      restoreAllAxes();
    };
    container.addEventListener("dblclick", restoreInitialView);

    return () => {
      container.removeEventListener("wheel", handleWheel, {
        capture: true,
      });
      container.removeEventListener("mousemove", showAxisTooltip);
      container.removeEventListener("mouseleave", hideAxisTooltip);
      container.removeEventListener("pointerdown", lockUserView);
      container.removeEventListener("dblclick", restoreInitialView);
      xAxis.removeEventListener(
        "intervalchange",
        repositionDepthReferenceLabels
      );
      // axisTooltip.remove();
      stageTooltip.remove();
      runtimeRef.current = undefined;
      stageAxisSync.remove();
      chart.dispose();
    };
    // structureKey and chartColors capture chart-structure and theme
    // dependencies. Data itself is appended by the effect below, without
    // recreating the WebGL chart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureKey, chartColors]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime || rows.length === 0) return;
    const forceAutoFit = pendingAutoFitRef.current;
    const shouldApplyCurrentInterval = !runtime.userViewLocked || forceAutoFit;

    const canAppend =
      !forceAutoFit &&
      !redrawOnRealtimeUpdate &&
      (runtime.syncedLength === 0 ||
        (rows.length >= runtime.syncedLength &&
          rows[runtime.syncedLength - 1] === runtime.lastSyncedRow));
    const startIndex = canAppend ? runtime.syncedLength : 0;

    if (!canAppend) {
      runtime.series.forEach(({ series }) => series.clear());
      clearStageMarkers(runtime);
      runtime.categoryLabels.length = 0;
      delete runtime.xOrigin;
      delete runtime.xStart;
      delete runtime.xEnd;
      runtime.xRanges.clear();
      runtime.yRanges.clear();
    }
    if (startIndex >= rows.length) return;

    const rowsToSyncLength = rows.length - startIndex;

    if (runtime.measuredDepthMode) {
      const yValues = new Float32Array(rowsToSyncLength);
      for (let localIndex = 0; localIndex < rowsToSyncLength; localIndex += 1) {
        yValues[localIndex] = toFiniteNumber(
          rows[startIndex + localIndex][runtime.yDescriptor.field]
        );
      }

      runtime.series.forEach(({ mnemonic, series }) => {
        const xValues = new Float32Array(rowsToSyncLength);
        for (let index = 0; index < rowsToSyncLength; index += 1) {
          xValues[index] = toFiniteNumber(rows[startIndex + index][mnemonic]);
        }
        series.appendSamples({ xValues, yValues });
      });

      syncStageMarkers(runtime, rows, startIndex, yValues);

      // Overlays carry their own (density, depth) points, independent of the log
      // rows. They are re-appended on every redraw of the measured-depth chart.
      let overlayDepthInterval: { start: number; end: number } | undefined;
      runtime.overlays.forEach((overlay) => {
        overlay.series.clear();
        const count = overlay.points.length;
        if (count === 0) return;
        const overlayXValues = new Float32Array(count);
        const overlayYValues = new Float32Array(count);
        for (let index = 0; index < count; index += 1) {
          overlayXValues[index] = overlay.points[index].value;
          overlayYValues[index] = overlay.points[index].md;
        }
        overlay.series.appendSamples({
          xValues: overlayXValues,
          yValues: overlayYValues,
        });
        const depthInterval = resolveOverlayInterval(
          overlay.points,
          (point) => point.md
        );
        if (depthInterval) {
          overlayDepthInterval = overlayDepthInterval
            ? mergeInterval(overlayDepthInterval, depthInterval)
            : depthInterval;
        }
      });

      const rowYInterval = resolveXInterval(yValues);
      const incomingYInterval =
        rowYInterval && overlayDepthInterval
          ? mergeInterval(rowYInterval, overlayDepthInterval)
          : rowYInterval || overlayDepthInterval;
      if (incomingYInterval) {
        const yInterval =
          startIndex === 0
            ? incomingYInterval
            : mergeInterval(
                runtime.yRanges.get("__measuredDepth"),
                incomingYInterval
              );
        runtime.yRanges.set("__measuredDepth", yInterval);
        const displayYInterval = toYAxisDisplayInterval(yInterval);
        // Depth increases downward, so invert the axis: smallest depth on top,
        // larger depth at the bottom.
        const orientedYInterval = {
          start: displayYInterval.end,
          end: displayYInterval.start,
        };

        runtime.yAxis.setDefaultInterval(orientedYInterval, {
          applyImmediately: startIndex === 0 && shouldApplyCurrentInterval,
        });
        if (shouldApplyCurrentInterval) {
          runtime.yAxis.setInterval({
            start: orientedYInterval.start,
            end: orientedYInterval.end,
            stopAxisAfter: true,
          });
        }
      }

      runtime.xAxes.forEach(({ groupKey, axis, mnemonics }) => {
        const rowInterval = resolveRowsMinMaxInterval(
          rows,
          startIndex,
          rows.length,
          mnemonics
        );
        // Fold in overlays that share this group's density axis.
        let overlayInterval: { start: number; end: number } | undefined;
        runtime.overlays.forEach((overlay) => {
          if (overlay.groupKey !== groupKey) return;
          const valueInterval = resolveOverlayInterval(
            overlay.points,
            (point) => point.value
          );
          if (valueInterval) {
            overlayInterval = overlayInterval
              ? mergeInterval(overlayInterval, valueInterval)
              : valueInterval;
          }
        });

        const incomingInterval =
          rowInterval && overlayInterval
            ? mergeInterval(rowInterval, overlayInterval)
            : rowInterval || overlayInterval;
        if (!incomingInterval) return;

        const interval =
          startIndex === 0
            ? incomingInterval
            : mergeInterval(runtime.xRanges.get(groupKey), incomingInterval);
        runtime.xRanges.set(groupKey, interval);
        const displayInterval = toPaddedDisplayInterval(interval);

        axis.setDefaultInterval(displayInterval, {
          applyImmediately: startIndex === 0 && shouldApplyCurrentInterval,
        });

        if (shouldApplyCurrentInterval) {
          axis.setInterval({
            start: displayInterval.start,
            end: displayInterval.end,
            stopAxisAfter: true,
          });
        }
      });

      runtime.syncedLength = rows.length;
      runtime.lastSyncedRow = rows[rows.length - 1];
      pendingAutoFitRef.current = false;
      return;
    }

    if (xDescriptor.kind === "date") {
      const dateOrigin =
        runtime.xOrigin ??
        getDateOrigin(
          startIndex === 0 ? rows : rows.slice(startIndex),
          xDescriptor
        );

      if (dateOrigin !== undefined && runtime.xOrigin !== dateOrigin) {
        runtime.xOrigin = dateOrigin;
        applyDateTimeTickStrategy(runtime.xAxis, dateOrigin, chartColors);
      }
    }

    const xValues = new Float32Array(rowsToSyncLength);
    for (let localIndex = 0; localIndex < rowsToSyncLength; localIndex += 1) {
      const absoluteIndex = startIndex + localIndex;
      const row = rows[absoluteIndex];
      xValues[localIndex] = getXValue(
        row,
        absoluteIndex,
        xDescriptor,
        runtime.xOrigin
      );
      if (xDescriptor.kind === "category") {
        runtime.categoryLabels[absoluteIndex] = String(
          row[xDescriptor.field] ?? ""
        );
      }
    }

    runtime.series.forEach(({ mnemonic, series }) => {
      const yValues = new Float32Array(rowsToSyncLength);
      for (let index = 0; index < rowsToSyncLength; index += 1) {
        yValues[index] = toFiniteNumber(rows[startIndex + index][mnemonic]);
      }
      series.appendSamples({ xValues, yValues });
    });

    syncStageMarkers(runtime, rows, startIndex, xValues);

    const incomingXInterval = resolveXInterval(xValues);
    if (incomingXInterval) {
      const xInterval =
        startIndex === 0
          ? incomingXInterval
          : mergeInterval(
              runtime.xStart === undefined || runtime.xEnd === undefined
                ? undefined
                : { start: runtime.xStart, end: runtime.xEnd },
              incomingXInterval
            );
      runtime.xStart = xInterval.start;
      runtime.xEnd = xInterval.end;

      if (runtime.xEdgeTicks) {
        const showEndEdge = xInterval.end !== xInterval.start;
        runtime.xEdgeTicks.start.forEach((tick) =>
          tick.setValue(xInterval.start).setVisible(true)
        );
        runtime.xEdgeTicks.end.forEach((tick) =>
          tick.setValue(xInterval.end).setVisible(showEndEdge)
        );
      }

      const displayXInterval = toDisplayInterval(
        xInterval,
        getSingleXValuePadding(xDescriptor)
      );

      runtime.xAxis.setDefaultInterval(displayXInterval, {
        applyImmediately: startIndex === 0 && shouldApplyCurrentInterval,
      });
      if (shouldApplyCurrentInterval) {
        runtime.xAxis.setInterval({
          start: displayXInterval.start,
          end: displayXInterval.end,
          stopAxisAfter: true,
        });
      }
    }

    const yAxisIntervals = runtime.yAxes
      .map(({ groupKey, axis, mnemonics }) => {
        const incomingInterval = resolveRowsMinMaxInterval(
          rows,
          startIndex,
          rows.length,
          mnemonics
        );
        if (!incomingInterval) return undefined;

        const interval =
          startIndex === 0
            ? incomingInterval
            : mergeInterval(runtime.yRanges.get(groupKey), incomingInterval);
        runtime.yRanges.set(groupKey, interval);

        return {
          groupKey,
          axis,
          displayInterval: toYAxisDisplayInterval(interval),
        };
      })
      .filter((item): item is NonNullable<typeof item> => Boolean(item));

    const displayedYAxisIntervals =
      alignIntervalsToSharedTickGrid(yAxisIntervals);

    displayedYAxisIntervals.forEach(({ axis, displayInterval }) => {
      axis.setDefaultInterval(displayInterval, {
        applyImmediately: startIndex === 0 && shouldApplyCurrentInterval,
      });

      if (shouldApplyCurrentInterval) {
        axis.setInterval({
          start: displayInterval.start,
          end: displayInterval.end,
          stopAxisAfter: true,
        });
      }
    });

    runtime.syncedLength = rows.length;
    runtime.lastSyncedRow = rows[rows.length - 1];
    pendingAutoFitRef.current = false;
  }, [
    data,
    xDescriptor,
    redrawOnRealtimeUpdate,
    measuredDepthDescriptor,
    measuredDepthMode,
    overlayKey,
    chartColors,
    autoFitKey,
  ]);

  // Depth reference lines (casing shoe): rebuild the dashed lines and their
  // titles whenever the depths change (e.g. on a unit switch) or the chart is
  // recreated (structureKey / chartColors mirror the chart-creation effect).
  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;

    const depthUnit = normalizeUnit(measuredDepthDescriptor?.unit);
    const precision = resolvePrecision(
      curvePrecisionsRef.current,
      measuredDepthDescriptor?.field
    );

    runtime.syncDepthReferenceLines(
      depthReferenceValues.map((value) => {
        const formattedDepth = formatNumber(value, precision);
        return {
          value,
          label: `Глубина ОК (${
            depthUnit ? `${formattedDepth} ${depthUnit}` : formattedDepth
          })`,
        };
      })
    );
    // structureKey / chartColors mirror the chart-creation effect so the lines
    // are re-added onto the freshly created chart; depthReferenceKey covers
    // depth/label changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureKey, chartColors, depthReferenceKey]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;

    runtime.series.forEach(({ mnemonic, series }) => {
      series.setVisible(!displayedHiddenSeries[mnemonic]);
    });
    runtime.overlays.forEach(({ mnemonic, series }) => {
      series.setVisible(!displayedHiddenSeries[mnemonic]);
    });
  }, [displayedHiddenSeries]);

  const handleLegendToggle = (mnemonic: string) => {
    const nextHiddenSeries = {
      ...displayedHiddenSeries,
      [mnemonic]: !displayedHiddenSeries[mnemonic],
    };

    window.localStorage.setItem(
      hiddenSeriesStorageKey,
      JSON.stringify(nextHiddenSeries)
    );
    if (!hiddenSeriesOverride) {
      setHiddenSeries(nextHiddenSeries);
    }
    onHiddenSeriesChange?.(nextHiddenSeries);
  };

  const setSeriesLineWidth = useCallback((mnemonic: string, width: number) => {
    const runtime = runtimeRef.current;
    if (!runtime) return false;

    const binding =
      runtime.series.find((item) => item.mnemonic === mnemonic) ||
      runtime.overlays.find((item) => item.mnemonic === mnemonic);
    if (!binding) return false;

    binding.series.setStrokeStyle((stroke) => stroke.setThickness(width));
    return true;
  }, []);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;

    runtime.series.forEach(({ series }) => {
      series.setStrokeStyle((stroke) => stroke.setThickness(NORMAL_LINE_WIDTH));
    });
    runtime.overlays.forEach(({ series }) => {
      series.setStrokeStyle((stroke) => stroke.setThickness(NORMAL_LINE_WIDTH));
    });

    if (highlightedSeriesMnemonic) {
      setSeriesLineWidth(highlightedSeriesMnemonic, HOVERED_LINE_WIDTH);
    }
  }, [highlightedSeriesMnemonic, setSeriesLineWidth]);

  const handleLegendEnter = (mnemonic: string) => {
    setSeriesLineWidth(mnemonic, HOVERED_LINE_WIDTH);
  };

  const handleLegendLeave = (mnemonic: string) => {
    setSeriesLineWidth(mnemonic, NORMAL_LINE_WIDTH);
  };

  return (
    <ChartContainer $isDialog={isDialog}>
      <h3>{getChartTitleTranslation(title)}</h3>
      {hasData && (
        <LegendBar>
          {legendItems.map((item) => (
            <LegendItem
              key={item.mnemonic}
              type="button"
              $hidden={Boolean(displayedHiddenSeries[item.mnemonic])}
              onClick={() => handleLegendToggle(item.mnemonic)}
              onMouseEnter={() => handleLegendEnter(item.mnemonic)}
              onMouseLeave={() => handleLegendLeave(item.mnemonic)}
            >
              <LegendMarker
                $color={item.color}
                $hidden={Boolean(displayedHiddenSeries[item.mnemonic])}
              />
              <span>{item.label}</span>
            </LegendItem>
          ))}
        </LegendBar>
      )}
      <ChartSurface ref={surfaceRef} $visible={hasData} />
      {!hasData && <NoDataMessage>{emptyMessage}</NoDataMessage>}
      <CloseButton
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
        role="button"
        aria-label="Закрыть график"
        $isDialog={isDialog}
      >
        <i className="pi pi-times" />
      </CloseButton>
      {onOpen && (
        <OpenButton
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          role="button"
          aria-label="Открыть в отдельном окне"
        >
          <i className="pi pi-external-link" />
        </OpenButton>
      )}
    </ChartContainer>
  );
};

export default LogChart;
